import { freeIncomeOpens, hasSalesDoor } from "./income-door.js";
/**
 * THE KERNEL'S FIVE, AND «ORGANIZAR LAS CATEGORÍAS» IS NOT ONE: the
 * categories stay the owner's. `category:write` is OWNER-ONLY
 * (backend/src/policy.ts), like `business:write` -- the business's shape is
 * the owner's -- and the Categorías screen asks for the ROLE in both clients.
 * Defining what is sold is the vertical's (`pedidos.prices`).
 */
export const KERNEL_CAPABILITIES = [
    {
        id: "ledger.record",
        module: null,
        kind: "record",
        question: "ledger",
        // `ledger:correct` IS THE CASH-REGISTER RULE (spec §3): the one Libro
        // answer that may change or delete a row anybody wrote. `expense:write`
        // stays what lets an egreso be filed and somebody be paid.
        //
        // `employee:read` because filing a payment means naming who it was for,
        // and the name comes off the list. It grants no writing: adding somebody,
        // the baja and the access are `business:write` and `member:invite`, both
        // the owner's.
        actions: [
            "ledger:read",
            "ledger:write",
            "ledger:correct",
            "expense:write",
            "request:answer",
            "employee:read",
            "party:read",
            "party:write",
            "expense:read",
        ],
    },
    {
        id: "ledger.income",
        module: null,
        kind: "record",
        question: "ledger",
        // NEITHER `expense:write` NOR `ledger:correct`: writes what comes in and
        // fixes nothing, not even its own typo -- whoever holds «Todo» does.
        // No `employee:read` either: somebody who cannot file an expense pays
        // nobody, so they have no business seeing what each person earns.
        // NOR `expense:read`: whoever only writes what comes in does not SEE what
        // goes out either -- no egresos, no debts to pay, and the side filter
        // hides. It is the one capability in the catalogue that reads half the
        // Libro.
        //
        // NI `party:read` NI `party:write`: anotar no es tener la agenda. El
        // autocompletar del contraparte SÍ le funciona, y entra por `ledger:write`
        // -- resolver un nombre es parte de escribir el movimiento, y así lo dice
        // la puerta de `GET /parties` (kernel/party/routes.ts). Lo que no tiene es
        // la pantalla: ni la lista, ni la ficha, ni el alta.
        actions: ["ledger:read", "ledger:write", "request:ask"],
    },
    {
        id: "ledger.view",
        module: null,
        kind: "view",
        question: "ledger",
        // NO `reports:read`: the charts are their own sentence,
        // and the accountant, who holds this alone, does not see them (VozIA gives them the income statement's totals).
        //
        // NOR `party:read`: seeing the Libro does not list the customers or open
        // a customer's or supplier's ficha beyond what the movement itself
        // shows. This is THE answer that stays in the Libro: the two lists of
        // people and the ficha belong to whoever works with them. The
        // counterparty's name is still written on each movement -- it just does
        // not open.
        //
        // `expense:read` SÍ, y es lo que la separa de «Solo anota ingresos»: el
        // contable mira las salidas y no escribe ninguna. Un contable que no ve
        // los egresos no es el contable de nadie.
        actions: ["ledger:read", "request:ask", "expense:read"],
    },
    {
        id: "ledger.analytics",
        module: null,
        kind: "view",
        question: "kernel",
        actions: ["reports:read"],
    },
    {
        // «MANEJA A LOS EMPLEADOS»: like the owner, except it cannot remove
        // itself. Everything about the people:
        // the alta, the personal file, the baja, the access and each one's
        // permissions -- including permissions this person does not hold. The
        // «not on themselves» part is the server's (kernel/employee/routes.ts,
        // kernel/membership/routes.ts), never only a hidden button.
        //
        // `member:invite` IS NOT IN OWNER_ONLY_ACTIONS: this sentence is what
        // grants it. What stays the owner's is the business itself --
        // `business:write`, categories, Add-ons, deleting it.
        id: "equipo.manage",
        module: null,
        kind: "record",
        // «¿QUÉ HACE CON LOS EMPLEADOS?»: one round
        // question with the payroll's two answers -- see PAYROLL_CAPABILITIES.
        // Without the add-on it is the one answer left, drawn as a box.
        question: "team",
        actions: ["employee:read", "employee:profile", "employee:write", "member:invite"],
    },
];
/**
 * TWO SENTENCES. «Define los planes y sus precios» is a capability of its
 * own: defining what is sold is the vertical's business, and every vertical
 * gets its own box for it (spec §2.4), so `plan:write` is not in
 * `OWNER_ONLY_ACTIONS`.
 */
export const DUES_CAPABILITIES = [
    {
        id: "cuotas.collect",
        module: "cuotas",
        kind: "record",
        question: "module",
        // Whoever cobra but does not anotar still writes the Libro by collecting,
        // even though they may not read it back.
        // `party:read`/`party:write` ARE THE KERNEL DIRECTORY'S and
        // `customer:write` keeps what is cuotas': enrolling WITH a plan and
        // ending an enrolment (`POST /customers`, `POST /subscriptions/:id/end`).
        // Two keys, so whoever keeps the Libro can be given the directory without
        // also being given a student's baja.
        actions: [
            "receivables:read",
            "obligation:write",
            "payment:write",
            "customer:write",
            "party:read",
            "party:write",
        ],
    },
    {
        id: "cuotas.plans",
        module: "cuotas",
        kind: "record",
        question: "module",
        actions: ["plan:write"],
    },
];
/**
 * THE TWO SIDES OF A TICKET, AND WHAT IT COSTS. The office keeps the
 * documents; the technician works the ones assigned to them; the price list
 * is a box of its own.
 *
 * NO «Ver la pantalla de cocina»: it names a room rather than a task, which
 * would quietly make `pedidos` the restaurant module.
 */
export const ORDERS_CAPABILITIES = [
    {
        id: "pedidos.handle",
        module: "pedidos",
        kind: "record",
        question: "orders",
        // THE DIRECTORY'S TWO KEYS. The office writes the ticket in somebody's
        // name -- `pedido/nuevo` demands a confirmed id, not a text -- and without
        // a key to `/parties` the Clientes tab both clients offer it would answer
        // 403.
        actions: ["order:read", "order:write", "party:read", "party:write"],
    },
    // THE TECHNICIAN (spec §5.1): reads what is assigned to them, works it,
    // collects it. Never `order:write` -- price, lines, description, quotes,
    // cancelling, assigning and undoing a cobro are the office's.
    {
        id: "pedidos.work",
        module: "pedidos",
        kind: "record",
        question: "orders",
        actions: ["order:mine", "order:work", "order:collect"],
    },
    {
        id: "pedidos.prices",
        module: "pedidos",
        kind: "record",
        // «¿QUÉ HACE CON LOS PRODUCTOS?» with inventory's two: «Corrige las
        // existencias» already carries `catalog:write`.
        question: "catalog",
        actions: ["catalog:write"],
    },
];
/**
 * INVENTORY'S TWO PHRASES (spec 2026-09-22-cuotas-inventario §4.6). Counting is split from
 * correcting on purpose: whoever only counts never sees what the shelf
 * SHOULD hold, so the number they write is what they saw. «Corrige» carries
 * `catalog:write` because a business with inventory and no pedidos (a salon
 * that sells gel) has nobody else who could create what it counts.
 */
export const INVENTORY_CAPABILITIES = [
    {
        id: "inventario.count",
        module: "inventario",
        kind: "record",
        question: "catalog",
        actions: ["stock:count"],
    },
    {
        id: "inventario.manage",
        module: "inventario",
        kind: "record",
        question: "catalog",
        actions: ["stock:write", "stock:count", "catalog:write"],
    },
];
/**
 * THE PAYROLL'S TWO SENTENCES (spec 2026-09-25): the payroll is not the
 * owner's alone.
 *
 * «Lleva la planilla» is all of it: contracts, planillas, closing and paying
 * (which files the egresos by itself), talonarios and reports, and the
 * personal file a payroll needs. NOT the risk rate: that is Add-ons, and
 * Add-ons is the owner's (`PUT /payroll/settings` asks `business:write`).
 *
 * «Ve los reportes de la planilla» is the accountant's: the month and the
 * year, nothing one planilla at a time. He gets it with his fixed grant
 * (`accountantCapabilities`), and anybody else only if the owner ticks it.
 *
 * ONE ROUND QUESTION WITH «Maneja a los empleados», like the tickets', since
 * whoever runs the payroll must also manage the employees: «¿Qué hace con
 * los empleados?» -- «Todo: los maneja y lleva
 * la planilla», «Solo los maneja», «Solo ve los reportes de la planilla»,
 * «Nada». Each answer contains what it needs, so ticking two said nothing.
 */
export const PAYROLL_CAPABILITIES = [
    {
        id: "planilla.manage",
        module: "planilla",
        kind: "record",
        question: "team",
        // EVERYTHING «Maneja a los empleados» carries, and the payroll on top:
        // whoever runs the payroll must manage the employees, because the
        // contract lives on the person's ficha.
        actions: [
            "employee:read",
            "employee:profile",
            "employee:write",
            "member:invite",
            "payroll:read",
            "payroll:write",
            "payroll:report",
        ],
    },
    {
        id: "planilla.report",
        module: "planilla",
        kind: "view",
        question: "team",
        actions: ["payroll:report"],
    },
];
/**
 * THE AGENDA, ONE SENTENCE. `order:*` are the common document's
 * doors (the same as pedidos.handle); `party:*` because an appointment starts
 * by choosing who; `employee:read` because booking is choosing with whom.
 */
export const APPOINTMENTS_CAPABILITIES = [
    {
        id: "citas.handle",
        module: "citas",
        kind: "record",
        question: "module",
        actions: ["order:read", "order:write", "party:read", "party:write", "employee:read"],
    },
];
/**
 * «PUEDE FACTURAR» (spec 2026-10-02 §3.5, borrador D §2.5): a box of the
 * Facturación add-on. Whoever invoices needs to see the customer with its RUC
 * and to complete its RUC and township when the customer asks for an invoice
 * by name, so the box carries the directory's two keys -- the arrangement
 * `pedidos.handle` has for the same reason: the owner ticks one box, not
 * two. `fiscal:issue` is issuing a sale's invoice; `fiscal:read` is the
 * invoices' own read (the card on the document, its PDF). Cancelling, the
 * credit note and retrying are «Todo»'s (`ledger:correct`), the cash rule.
 */
export const INVOICING_CAPABILITIES = [
    {
        id: "facturacion.issue",
        module: "facturacion",
        kind: "record",
        question: "module",
        actions: ["fiscal:issue", "fiscal:read", "party:read", "party:write"],
    },
    // «VE LAS FACTURAS» (spec 2026-10-02, entrega 4 §5): the list, the ficha, the
    // PDF and the CSV -- the receiver travels frozen inside each invoice, so it
    // needs neither the tickets nor the directory. The accountant's where
    // Facturación is on (`accountantCapabilities`).
    {
        id: "facturacion.read",
        module: "facturacion",
        kind: "view",
        question: "module",
        actions: ["fiscal:read"],
    },
];
/** Kernel first, then each module in MODULE_IDS order. The catalogue's own order. */
export const ALL_CAPABILITIES = [
    ...KERNEL_CAPABILITIES,
    ...DUES_CAPABILITIES,
    ...ORDERS_CAPABILITIES,
    ...INVENTORY_CAPABILITIES,
    ...PAYROLL_CAPABILITIES,
    ...APPOINTMENTS_CAPABILITIES,
    ...INVOICING_CAPABILITIES,
];
export const CAPABILITY_IDS = ALL_CAPABILITIES.map((c) => c.id);
/** The questions whose answers exclude each other: at most one row of each may be granted. */
export const RADIO_QUESTIONS = ["ledger", "orders", "team", "catalog"];
/**
 * The order a round question reads in, where the catalogue's is not it:
 * «Todo» first, as every question here reads. The team's and the catalog's
 * answers live in two blocks of the catalogue each (kernel and payroll,
 * pedidos and inventario), so without this «Solo…» would come before «Todo».
 */
export function answerRank(id) {
    const ORDERED = [
        "planilla.manage", "equipo.manage", "planilla.report",
        "inventario.manage", "pedidos.prices", "inventario.count",
    ];
    const at = ORDERED.indexOf(id);
    return at === -1 ? ORDERED.length + ALL_CAPABILITIES.findIndex((c) => c.id === id) : at;
}
export const GRANT_TOO_MANY_ANSWERS = "Elige una sola respuesta por pregunta.";
/**
 * THE CHARTS BELONG TO THE LIBRO, and without a Libro answer they mean
 * nothing.
 *
 * `ledger.analytics` is a loose kernel box, so it could be ticked with
 * «Nada» in the Libro -- and then Analíticas would open and BREAK whole:
 * that screen reads the categories with the trend, and `GET /categories`
 * asks `ledger:read`, which no other sentence grants. Not half an empty
 * screen: the load error.
 *
 * So it cannot be ticked. Not `ledger:read` for the charts (that would also
 * open the Libro and Deudas), nor hiding Analíticas from whoever holds it
 * (that confuses whoever ticked it). The sentence is what the owner reads,
 * not a restriction.
 */
export const GRANT_ANALYTICS_WITHOUT_LEDGER = "Las gráficas son del Libro: elige antes qué hace esta persona con el Libro.";
/**
 * Las casillas que no significan nada sin una respuesta del Libro.
 *
 * UNA LISTA Y NO UN `if`, porque la pregunta se hace en tres sitios —— el
 * servidor al conceder y los dos formularios al dibujar —— y la segunda que
 * nazca tiene que aparecer en los tres a la vez. Hoy tiene una sola entrada.
 */
export const NEEDS_LEDGER_ANSWER = ["ledger.analytics"];
/**
 * A GRANT SETTLED ONTO TODAY'S QUESTIONS (spec 2026-09-25). What a grant
 * written before may carry, and what it becomes:
 *
 * - TWO ANSWERS TO ONE ROUND QUESTION -- inventory's three were once boxes.
 *   They become the one that contains them: «Corrige las existencias» holds
 *   both «define» and «cuenta», so any two of the three become it («define +
 *   cuenta» goes up to «Todo», never down to one of them). For the team, the larger by `answerRank`.
 * - «LLEVA LA PLANILLA» WITH THE PAYROLL OFF. Its team half still works --
 *   those routes do not ask for the module -- so it reads as what it still
 *   is, «Maneja a los empleados», and saves as that.
 * - «TODO CON LAS EXISTENCIAS» WITH INVENTORY OFF. It still writes the
 *   catalogue, so where pedidos is on it reads as «Define los productos».
 *   Two catalogue answers settle into one only
 *   while inventory is on: off, «cuenta» is gone and «define» stays.
 * - «SOLO ANOTA INGRESOS» WHERE INCOME HAS ITS OWN DOOR (income-door.ts). The
 *   Libro's income-only answer has no place there (a loose income is for
 *   whoever also writes expenses), so it reads the Libro and gets the door
 *   the sales moved to, the tickets, unless that was already answered.
 *
 * Nothing else ticked is ever narrowed. Both forms seed their state with it, and
 * the server settles what it is sent before checking it, so a phone still
 * on the old bundle is not refused for sending both boxes.
 */
export function settledGrant(ids, modules, assignsWork) {
    const chosen = new Set(ids);
    if (chosen.has("planilla.manage") && !modules.includes("planilla")) {
        chosen.delete("planilla.manage");
        chosen.add("equipo.manage");
    }
    if (chosen.has("inventario.manage") && !modules.includes("inventario") && modules.includes("pedidos")) {
        chosen.delete("inventario.manage");
        chosen.add("pedidos.prices");
    }
    const catalog = ["pedidos.prices", "inventario.count", "inventario.manage"].filter((id) => chosen.has(id));
    if (catalog.length > 1 && modules.includes("inventario")) {
        for (const id of catalog)
            chosen.delete(id);
        chosen.add("inventario.manage");
    }
    const team = ALL_CAPABILITIES.filter((c) => c.question === "team" && chosen.has(c.id))
        .map((c) => c.id)
        .sort((a, b) => answerRank(a) - answerRank(b));
    for (const id of team.slice(1))
        chosen.delete(id);
    // «SOLO ANOTA INGRESOS» WHERE INCOME HAS ITS OWN DOOR: the Libro is read, and
    // the income is written where it lives -- the documents.
    // Whatever was already answered there is kept. Migration 0084 is this rule in SQL.
    if (chosen.has("ledger.income") && hasSalesDoor(modules)) {
        chosen.delete("ledger.income");
        chosen.add("ledger.view");
        if (modules.includes("pedidos") && !chosen.has("pedidos.handle") && !chosen.has("pedidos.work")) {
            chosen.add(assignsWork ? "pedidos.work" : "pedidos.handle");
        }
    }
    return ALL_CAPABILITIES.map((c) => c.id).filter((id) => chosen.has(id));
}
/**
 * THE MODULES WHOSE TWO BOXES ARE ASKED AS ONE ROUND QUESTION, from all to
 * nothing like the others. «¿Qué hace con las cuotas?»: Todo / Solo cobra / Solo define
 * los planes / Nada -- the catalogue's own shape.
 *
 * A WAY OF ASKING, NOT NEW CAPABILITIES. «Todo» saves the two boxes it always
 * means, so a grant reads and is checked as two boxes: the server, the doors,
 * the migrations and every grant given know nothing of it. Only the forms
 * ask differently. The alternative -- a `cuotas.manage` like
 * `inventario.manage` -- would have had to be taught to every place that asks
 * for `cuotas.collect` by name, and the one forgotten would lock «Todo» out.
 */
export const PAIRED_QUESTIONS = {
    cuotas: { work: "cuotas.collect", define: "cuotas.plans" },
};
export const PAIRED_MODULES = Object.keys(PAIRED_QUESTIONS);
/** Which of the four answers a grant gives to one module's paired question. */
export function pairAnswer(module, ids) {
    const pair = PAIRED_QUESTIONS[module];
    const work = ids.includes(pair.work);
    const define = ids.includes(pair.define);
    return work && define ? "all" : work ? "work" : define ? "define" : "none";
}
/** The grant with that answer given, the rest untouched, in catalogue order. */
export function withPairAnswer(module, ids, answer) {
    const pair = PAIRED_QUESTIONS[module];
    const chosen = new Set(ids);
    chosen.delete(pair.work);
    chosen.delete(pair.define);
    if (answer === "all" || answer === "work")
        chosen.add(pair.work);
    if (answer === "all" || answer === "define")
        chosen.add(pair.define);
    return ALL_CAPABILITIES.map((c) => c.id).filter((id) => chosen.has(id));
}
/** Si esta lista de frases se puede conceder, y la razón de que no. */
export function grantProblem(ids) {
    const tooMany = RADIO_QUESTIONS.some((q) => ALL_CAPABILITIES.filter((c) => c.question === q && ids.includes(c.id)).length > 1);
    if (tooMany)
        return GRANT_TOO_MANY_ANSWERS;
    const answersLedger = ALL_CAPABILITIES.some((c) => c.question === "ledger" && ids.includes(c.id));
    if (!answersLedger && NEEDS_LEDGER_ANSWER.some((id) => ids.includes(id))) {
        return GRANT_ANALYTICS_WITHOUT_LEDGER;
    }
    return null;
}
/**
 * The capabilities an owner may offer: the kernel's, plus those of the
 * modules given, minus `pedidos.work` where nobody assigns anything and
 * `ledger.income` where income has its own door (`hasSalesDoor`).
 *
 * `assignsWork` IS DECIDED BY THE CALLER from `assignsWork(type)` (faces.ts)
 * (type-words.ts's `offeredCapabilities` does it for every screen), because
 * this file is a leaf `business-type.ts` depends on. «Solo hace los tickets
 * que le asignen» exists only where tickets have workers (spec §1.1), which
 * today is the taller.
 */
export function capabilitiesFor(modules, assignsWork) {
    return ALL_CAPABILITIES.filter((c) => (c.module === null || modules.includes(c.module)) &&
        (c.id !== "pedidos.work" || assignsWork) &&
        // Where sales have their own door, «Solo anota ingresos» is not offered: a loose income there is for whoever also writes expenses.
        (c.id !== "ledger.income" || !hasSalesDoor(modules)));
}
/**
 * WHAT THE ACCOUNTANT IS GIVEN, the one grant nobody chooses, and he never
 * writes in the Libro (spec 2026-09-01; spec 2026-09-25 §4; spec 2026-10-02,
 * entrega 4 §5): the Libro to read, the payroll's reports where
 * the payroll is on, and the invoices where Facturación is. Only there,
 * because a grant is checked against what the business offers
 * (`assertGrantable`), and an unoffered id refuses the whole invitation. A
 * business that turns one of them on later gives it to him then
 * (`updateBusiness`).
 */
export function accountantCapabilities(modules) {
    return [
        "ledger.view",
        ...(modules.includes("planilla") ? ["planilla.report"] : []),
        ...(modules.includes("facturacion") ? ["facturacion.read"] : []),
    ];
}
export function can(m, id) {
    if (m === null)
        return false;
    return m.role === "owner" || m.capabilities.includes(id);
}
/**
 * Whether this person holds one of the backend's own permission strings.
 *
 * ASK FOR THE ACTION, NOT FOR THE PHRASE. A door that names a capability --
 * `can(m, "ledger.record")` -- reads as «is this the person who keeps the
 * Libro?» rather than as «may this end in a write?». That works only while
 * there is exactly one phrase per action, and the kernel has more than one.
 *
 * `actions` is the same list the backend derives its policy from (see this
 * file's own header), so a client door asking through here asks EXACTLY what
 * the route it ends in will ask -- which is the rule this app holds every
 * control to: a button that always ends in a 403 is worse than no button.
 *
 * The owner answers yes to everything, as in `can`: their list is empty on
 * purpose and the role is the permission.
 *
 * `action` IS THE CATALOGUE'S OWN UNION (`CapabilityAction`), not `string`.
 * A door is a sentence typed out by hand in one of ~25 places, and with a
 * `string` parameter a slip -- «expence:write» -- would compile, return
 * false and shut a screen somebody was entitled to, with nothing going red
 * anywhere. An action nobody can hold is unsayable rather than silently
 * refused.
 */
export function holdsAction(m, action) {
    if (m === null)
        return false;
    if (m.role === "owner")
        return true;
    // `c.actions` widened back to `readonly string[]` for the lookup only: the
    // rows are `as const`, so each one's `actions` is its own tuple type and
    // `includes` on the union of them asks for an intersection of them all --
    // which is `never`. The union this searches FOR is `CapabilityAction`, read
    // off those same tuples; the widening is inside the search, not on it.
    return ALL_CAPABILITIES.some((c) => m.capabilities.includes(c.id) && c.actions.includes(action));
}
/**
 * Whether this person sees the OUT side of the book at all.
 *
 * ONE QUESTION, ESCRITA UNA VEZ para los dos clientes: el filtro de lado del
 * Libro, las pestañas de Deudas, el neto y la tarjeta de egresos de Inicio y
 * la columna de egresos de «Lo que llevas» preguntan esto y nada más. El
 * servidor estrecha las filas por su cuenta (`sidesOf`, http/access.ts); esto
 * es lo que evita que una pantalla dibuje un filtro con una sola respuesta
 * posible.
 *
 * `expense:read` Y NO `expense:write`: ver y escribir son dos cosas, y el
 * contable es justo el caso que lo demuestra —— mira todas las salidas y no
 * anota ninguna (spec 2026-09-19-cuotas-solo-lo-que-entra §2).
 */
export function seesExpenses(m) {
    return holdsAction(m, "expense:read");
}
export function partyReach(m) {
    const reads = holdsAction(m, "party:read");
    return { finds: reads || holdsAction(m, "ledger:write"), reads, creates: holdsAction(m, "party:write") };
}
/**
 * The one money window, measured against the person in front of it.
 *
 * THE SIDE DECIDES EVERYTHING, and a debt is no exception: a debt to pay is
 * money that will go out, committed in writing with its date, so whoever
 * cannot write an egreso cannot commit one either. `POST /debts` demands the
 * same (`kernel/debts/repository.ts`), which is what makes this door the
 * honest question and not a courtesy of the client.
 *
 * `side` is the side the window opens on: the «+» or the list chose it
 * before opening, and the window never asks it.
 *
 * The doors that open ALREADY FIXED -- correcting a movement, cobrar un
 * pedido, un lavado o una deuda, «Pagar a…» -- ask none of this: their own
 * caller decided, and they never draw either question.
 *
 * `income` decides whether money IN is a door here at all (income-door.ts).
 */
export function moneyDoor(m, at) {
    const writes = holdsAction(m, "ledger:write");
    const expenses = writes && holdsAction(m, "expense:write");
    // `income` is the door's own answer: the free window asks `freeIncomeOpens`, a
    // debt's own door always may -- collecting what is owed is not new income.
    const opens = at.side === "IN" ? writes && at.income : expenses;
    return { opens, asksPaid: opens };
}
/** Whether the plain money window has any side to open for this person here: the Libro's and Inicio's door. */
export function recordsMoney(m, modules) {
    const income = freeIncomeOpens(holdsAction(m, "expense:write"), modules);
    return moneyDoor(m, { side: "IN", income }).opens || moneyDoor(m, { side: "OUT", income }).opens;
}
